import { BaseMapListItem } from '@/components/BaseMapListItem';
import SridSelector from '@/components/SridSelector';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { validateBaseMapUrl } from '@/core/baseMap';
import { execute } from '@/graphql/execute';
import type { CustomBaseMap, GetProjectQuery, Project } from '@/graphql/graphql';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { analytics } from '@/lib/analytics';
import {
  addProjectBaseMapMutation,
  archiveProjectMutation,
  deleteProjectBaseMapMutation,
  deleteProjectMutation,
  updateProjectBaseMapMutation,
  updateProjectMutation,
} from '@/mutations/project.mutation';
import { archivePreviewQuery, getProjectQuery } from '@/queries/project.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Loader } from 'lucide-react';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface iProjectEditProps {}

const ProjectEdit: FunctionComponent<iProjectEditProps> = () => {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) throw new Error('Project ID is required');

  const {
    project: initialProject,
    projectLoading,
    href,
  } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();
  const { t } = useTranslation('project');
  usePageMeta({ title: [t('edit.title'), initialProject?.name] });
  const { data: projectData, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId! }),
  });
  const project = projectData?.project;

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className='w-full h-full flex items-center justify-center'>
          <Loader className='w-4 h-4 animate-spin' />
        </div>
      );
    }
    if (!project) return <div>{t('edit.notFound')}</div>;
    return <ProjectForm project={project} />;
  }, [isLoading, project, href]);

  return (
    <PageSkeleton
      steps={
        projectLoading
          ? [{ label: t('edit.loading'), href }]
          : [
              { label: initialProject.name, href },
              {
                label: t('edit.title'),
                href: `${href}/edit`,
              },
            ]
      }>
      <div>{content}</div>
    </PageSkeleton>
  );
};

const types = {
  XYZ: 'XYZ Tiles',
  WMS: 'WMS',
  MAPBOX_STYLE: 'Mapbox Style',
};

const ProjectForm = ({ project }: { project: GetProjectQuery['project'] }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('project');
  const [showAddBaseMap, setShowAddBaseMap] = useState(false);
  const [editingBaseMap, setEditingBaseMap] = useState<CustomBaseMap | null>(null);
  const baseMaps = (project.baseMaps as CustomBaseMap[]) ?? [];

  const formik = useFormik({
    initialValues: {
      name: project.name,
      description: project.description ?? '',
      srid: project.srid,
      initialPosition: project.initialPosition,
      initialAngle: project.initialAngle,
    },
    onSubmit: async (values, helpers) => {
      const id = toast.loading(t('edit.updatingProject'));
      try {
        if (values.initialPosition) values.initialPosition.srid = values.srid;
        await execute(updateProjectMutation, { input: { id: project.id, ...values } });
        helpers.resetForm({
          values: {
            name: values.name,
            description: values.description,
            srid: values.srid,
            initialPosition: values.initialPosition,
            initialAngle: values.initialAngle,
          },
        });
        queryClient.invalidateQueries({ queryKey: ['project', project.id] });
        queryClient.invalidateQueries({ queryKey: ['projects', project.organizationId] });
        toast.success(t('edit.projectUpdated'), { id });
        analytics.capture('Project Updated');
      } catch (error) {
        toast.error(t('edit.failedUpdateProject'), { id });
      }
    },
  });
  console.log(baseMaps);

  const _handleDelete = async () => {
    const id = toast.loading(t('edit.deletingProject'));
    analytics.capture('Project Deleted');
    try {
      await execute(deleteProjectMutation, { id: project.id });
      navigate('/', { replace: true });
      queryClient.invalidateQueries({ queryKey: ['projects', project.organizationId] });
      toast.success(t('edit.projectDeleted'), { id });
    } catch (error) {
      toast.error(t('edit.failedDeleteProject'), { id });
    }
  };
  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='flex flex-col gap-4 max-w-md p-4 mx-auto'>
        <div id='onboarding-project-settings' className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-name'>{t('edit.projectName')}</Label>
          <Input id='project-name' placeholder={t('edit.projectNamePlaceholder')} {...formik.getFieldProps('name')} />
        </div>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-description'>{t('edit.projectDescription')}</Label>
          <Textarea
            id='project-description'
            placeholder={t('edit.projectDescriptionPlaceholder')}
            {...formik.getFieldProps('description')}
          />
        </div>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-srid'>{t('edit.epsg')}</Label>
          <SridSelector
            className='w-full'
            value={formik.values.srid.toString()}
            onChange={(value) => formik.setFieldValue('srid', parseInt(value))}
          />
        </div>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-srid'>{t('edit.initialPosition')}</Label>
          <div className='grid grid-cols-3 gap-2'>
            <Input
              type='number'
              placeholder={t('edit.placeholderX')}
              {...formik.getFieldProps('initialPosition.coords[0]')}
            />
            <Input
              type='number'
              placeholder={t('edit.placeholderY')}
              {...formik.getFieldProps('initialPosition.coords[1]')}
            />
            <Input
              type='number'
              placeholder={t('edit.placeholderZ')}
              {...formik.getFieldProps('initialPosition.coords[2]')}
            />
          </div>
          <Label htmlFor='project-srid'>{t('edit.initialAngle')}</Label>
          <Input type='number' placeholder={t('edit.placeholderAngle')} {...formik.getFieldProps('initialAngle')} />
        </div>
        {/* Base Maps */}
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label>{t('edit.customBaseMaps')}</Label>
          {baseMaps.length === 0 && <p className='text-sm text-muted-foreground'>{t('edit.noBaseMaps')}</p>}
          {baseMaps.map((bm) => (
            <BaseMapListItem
              key={bm.id}
              name={bm.name}
              type={bm.type}
              url={bm.url}
              onEdit={() => setEditingBaseMap(bm)}
              onRemove={async () => {
                try {
                  await execute(deleteProjectBaseMapMutation, { projectId: project.id, baseMapId: bm.id });
                  queryClient.invalidateQueries({ queryKey: ['project', project.id] });
                  toast.success(t('edit.baseMapRemoved'));
                } catch {
                  toast.error(t('edit.failedRemoveBaseMap'));
                }
              }}
            />
          ))}
          <Button type='button' variant='outline' onClick={() => setShowAddBaseMap(true)}>
            {t('edit.addBaseMap')}
          </Button>
        </div>
        {(project as any).storageUsedBytes != null && (
          <div className='grid w-full max-w-md items-center gap-1.5 text-sm'>
            <Label>{t('edit.storage')}</Label>
            <div className='flex items-baseline justify-between'>
              <span className='text-slate-700 dark:text-stone-300 font-mono'>
                {formatBytes((project as any).storageUsedBytes)}
              </span>
              {(project as any).ephemeralBytes > 0 && (
                <span className='text-xs text-slate-500 dark:text-stone-400 italic'>
                  {t('edit.includingEphemeral', { size: formatBytes((project as any).ephemeralBytes) })}
                </span>
              )}
            </div>
            <PhantomRetentionBanner project={project} />
          </div>
        )}
        <Button disabled={formik.isSubmitting || !formik.dirty} type='submit' className='w-full'>
          {t('edit.submit')}
        </Button>
        <ArchiveProjectButton project={project} />
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant='destructive'>{t('edit.deleteProject')}</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('edit.confirmDeleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('edit.confirmDeleteDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('edit.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={_handleDelete}>{t('edit.continue')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <BaseMapFormDialog open={showAddBaseMap} onOpenChange={setShowAddBaseMap} projectId={project.id} />
      <BaseMapFormDialog
        open={!!editingBaseMap}
        onOpenChange={(open) => !open && setEditingBaseMap(null)}
        projectId={project.id}
        baseMap={editingBaseMap}
      />
    </form>
  );
};

function BaseMapFormDialog({
  open,
  onOpenChange,
  projectId,
  baseMap,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  baseMap?: CustomBaseMap | null;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('project');
  const formik = useFormik({
    initialValues: {
      name: baseMap?.name ?? '',
      type: baseMap?.type ?? 'XYZ',
      url: baseMap?.url ?? '',
      tileSize: baseMap?.tileSize ?? 256,
      wmsVersion: baseMap?.wmsVersion ?? '1.3.0',
      wmsLayers: baseMap?.wmsLayers ?? '',
      format: baseMap?.format ?? 'image/png',
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const validationError = validateBaseMapUrl(values.type as any, values.url);
      if (validationError) {
        toast.error(validationError);
        return;
      }
      try {
        if (baseMap) {
          await execute(updateProjectBaseMapMutation, {
            projectId,
            baseMapId: baseMap.id,
            input: values,
          });
        } else {
          await execute(addProjectBaseMapMutation, {
            projectId,
            input: values,
          });
        }
        queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        onOpenChange(false);
        toast.success(baseMap ? t('baseMapDialog.baseMapUpdated') : t('baseMapDialog.baseMapAdded'));
      } catch {
        toast.error(t('baseMapDialog.failedSaveBaseMap'));
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>{baseMap ? t('baseMapDialog.editTitle') : t('baseMapDialog.addTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit}>
          <div className='grid gap-4 py-4'>
            <div className='grid gap-2'>
              <Label htmlFor='bm-name'>{t('baseMapDialog.name')}</Label>
              <Input id='bm-name' placeholder={t('baseMapDialog.namePlaceholder')} {...formik.getFieldProps('name')} />
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='bm-type'>{t('baseMapDialog.type')}</Label>
              <select
                id='bm-type'
                className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                {...formik.getFieldProps('type')}>
                {Object.entries(types).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className='grid gap-2'>
              <Label htmlFor='bm-url'>{t('baseMapDialog.url')}</Label>
              <Input
                id='bm-url'
                placeholder={
                  formik.values.type === 'XYZ'
                    ? 'https://tile.example.com/{z}/{x}/{y}.png'
                    : formik.values.type === 'WMS'
                      ? 'https://wms.example.com/service'
                      : 'mapbox://styles/username/style-id'
                }
                {...formik.getFieldProps('url')}
              />
            </div>
            {formik.values.type === 'WMS' && (
              <>
                <div className='grid gap-2'>
                  <Label htmlFor='bm-wms-layers'>{t('baseMapDialog.wmsLayers')}</Label>
                  <div className='flex gap-2'>
                    <Input
                      id='bm-wms-layers'
                      placeholder={t('edit.placeholderLayers')}
                      className='flex-1'
                      {...formik.getFieldProps('wmsLayers')}
                    />
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={async () => {
                        try {
                          const separator = formik.values.url.includes('?') ? '&' : '?';
                          const capsUrl = `${formik.values.url}${separator}service=WMS&request=GetCapabilities`;
                          const res = await fetch(capsUrl);
                          const text = await res.text();
                          const parser = new DOMParser();
                          const doc = parser.parseFromString(text, 'text/xml');
                          const layerEls = doc.querySelectorAll('Layer > Name');
                          const layerNames = Array.from(layerEls)
                            .map((el) => el.textContent)
                            .filter(Boolean);
                          if (layerNames.length > 0) {
                            formik.setFieldValue('wmsLayers', layerNames.join(','));
                            toast.success(t('baseMapDialog.foundLayers', { count: layerNames.length }));
                          } else {
                            toast.error(t('baseMapDialog.noLayersFound'));
                          }
                        } catch {
                          toast.error(t('baseMapDialog.failedFetchWms'));
                        }
                      }}>
                      {t('baseMapDialog.fetchLayers')}
                    </Button>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-2'>
                  <div className='grid gap-2'>
                    <Label htmlFor='bm-wms-version'>{t('baseMapDialog.version')}</Label>
                    <select
                      id='bm-wms-version'
                      className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                      {...formik.getFieldProps('wmsVersion')}>
                      <option value='1.3.0'>1.3.0</option>
                      <option value='1.1.1'>1.1.1</option>
                    </select>
                  </div>
                  <div className='grid gap-2'>
                    <Label htmlFor='bm-format'>{t('baseMapDialog.format')}</Label>
                    <select
                      id='bm-format'
                      className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                      {...formik.getFieldProps('format')}>
                      <option value='image/png'>PNG</option>
                      <option value='image/jpeg'>JPEG</option>
                    </select>
                  </div>
                </div>
              </>
            )}
            {(formik.values.type === 'XYZ' || formik.values.type === 'WMS') && (
              <div className='grid gap-2'>
                <Label htmlFor='bm-tilesize'>{t('baseMapDialog.tileSize')}</Label>
                <select
                  id='bm-tilesize'
                  className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm'
                  value={formik.values.tileSize}
                  onChange={(e) => formik.setFieldValue('tileSize', parseInt(e.target.value))}>
                  <option value={256}>256px</option>
                  <option value={512}>512px</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
              {t('baseMapDialog.cancel')}
            </Button>
            <Button type='submit' disabled={formik.isSubmitting || !formik.values.name || !formik.values.url}>
              {baseMap ? t('baseMapDialog.update') : t('baseMapDialog.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const formatBytes = (bytes: number) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let value = bytes;
  while (value >= 1000 && i < units.length - 1) {
    value /= 1000;
    i++;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
};

const formatDate = (d: string | Date | null | undefined, lang: string) =>
  d ? new Date(d).toLocaleDateString(lang) : '—';

const PhantomRetentionBanner: FunctionComponent<{ project: GetProjectQuery['project'] }> = ({ project }) => {
  const { t, i18n } = useTranslation('project');
  const retentionUntil = (project as any).retentionUntil as string | Date | null | undefined;
  const archiveBytes = ((project as any).archiveBytes as number | null | undefined) ?? 0;
  if (!retentionUntil || new Date(retentionUntil) <= new Date()) return null;
  const phantomBytes = Math.floor(archiveBytes * 0.3);
  if (phantomBytes <= 0) return null;
  return (
    <p className='text-xs text-amber-700 dark:text-amber-400 italic'>
      {t('edit.phantomRetentionNote', {
        size: formatBytes(phantomBytes),
        date: formatDate(retentionUntil, i18n.language),
        interpolation: { escapeValue: false },
      })}
    </p>
  );
};

const ArchiveProjectButton: FunctionComponent<{ project: GetProjectQuery['project'] }> = ({ project }) => {
  const { t, i18n } = useTranslation('project');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: previewData, isLoading: previewLoading } = useQuery({
    queryKey: ['archivePreview', project.id],
    queryFn: () => execute(archivePreviewQuery, { projectId: project.id }),
    enabled: open,
  });

  // Hide the button entirely when the project is already archived or transitioning.
  // (The project query filter normally already excludes ARCHIVED projects from the
  // sidebar, but ARCHIVING is still navigable while the worker runs.)
  if (project.archiveStatus && project.archiveStatus !== 'NOT_ARCHIVED') {
    return null;
  }

  // 90-day cooldown after a previous archive — backend mutation rejects too,
  // but we surface it in the UI to prevent the click and explain why.
  const retentionUntil = (project as any).retentionUntil as string | Date | null | undefined;
  const inCooldown = retentionUntil && new Date(retentionUntil) > new Date();
  if (inCooldown) {
    return (
      <Button
        type='button'
        variant='outline'
        disabled
        title={t('archive.cooldownTooltip', {
          date: formatDate(retentionUntil, i18n.language),
          interpolation: { escapeValue: false },
        })}>
        {t('archive.cooldownButton', {
          date: formatDate(retentionUntil, i18n.language),
          interpolation: { escapeValue: false },
        })}
      </Button>
    );
  }
  const preview = previewData?.archivePreview;
  const blocked = (preview?.blockingJobs?.length || 0) > 0;

  const _handleArchive = async () => {
    const id = toast.loading(t('archive.archivingInProgress'));
    try {
      await execute(archiveProjectMutation, { id: project.id });
      queryClient.invalidateQueries({ queryKey: ['projects', project.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      toast.success(t('archive.archiveSuccess'), { id });
      setOpen(false);
    } catch (err: any) {
      toast.error(t('archive.archiveFailed', { error: err?.message ?? 'unknown' }), { id });
    }
  };

  console.log(console.log(i18n.language));
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button type='button' variant='outline'>
          {t('archive.button')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('archive.modalTitle')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='space-y-2 text-sm'>
              {previewLoading && <p>{t('edit.loading')}</p>}
              {preview && !blocked && (
                <>
                  <p>
                    {t('archive.archivableBytes', {
                      archivable: formatBytes(preview.archivableBytes),
                      count: preview.archivableFileCount,
                      nonArchivable: formatBytes(preview.nonArchivableBytes),
                    })}
                  </p>
                  <p className='text-emerald-700 dark:text-emerald-400'>
                    {t('archive.quotaImpact', {
                      archiveQuota: formatBytes(preview.quotaAfterArchive),
                      originalSize: formatBytes(preview.archivableBytes + preview.nonArchivableBytes),
                    })}
                  </p>
                  <p>{t('archive.restoreTimeWarning')}</p>
                  <p>
                    {t('archive.phantomRetentionWarning', {
                      heldStorage: formatBytes(preview.quotaAfterArchive),
                      date: formatDate(preview.retentionEndsAt, i18n.language),
                      interpolation: { escapeValue: false },
                    })}
                  </p>
                  <p className='text-amber-700 dark:text-amber-400'>{t('archive.linksWillReset')}</p>
                </>
              )}
              {preview && blocked && (
                <>
                  <p className='text-destructive'>
                    {t('archive.blockingJobsTitle', { count: preview.blockingJobs.length })}
                  </p>
                  <p>{t('archive.blockingJobsHelp')}</p>
                  <ul className='list-disc pl-4'>
                    {preview.blockingJobs.map((j) => (
                      <li key={j.id}>
                        {j.type} — {j.status}
                        {j.label ? ` (${j.label})` : ''}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('edit.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={_handleArchive} disabled={previewLoading || blocked}>
            {t('archive.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ProjectEdit;
